import React from 'react';
import type { ComponentConfig } from '@puckeditor/core';
import styled from 'styled-components';

import { createSpacingFields } from '../fields/spacing-fields';
import { CONTAINER_TEXT_COLOR_OPTIONS, containerColorStyle, INHERIT_COLOR } from '../fields/container-colors';
import type { ContainerTextColor } from '../fields/container-colors';
import { BackgroundField, backgroundStyle, BACKGROUND_AI_SCHEMA, DEFAULT_BACKGROUND } from '../fields';
import type { BackgroundValue } from '../fields';
import { BLOCK_SPACING, CONTAINER_MIN_HEIGHTS, CONTAINER_WIDTHS, lookup, spacingToCss, token } from '../fields/tokens';
import type { BoxSpacing } from '../fields/tokens';
import { selectField } from '../fields/widgets/select-field';

import { starterChild } from './slot';
import type { SlotProps, StarterChild } from './slot';

/**
 * Scrim presets layered over the Hero's background image so overlaid text stays legible.
 *
 * Each level is wrapped in a `--dcm-*` custom property with the literal as its fallback, matching
 * `CONTAINER_MIN_HEIGHTS`/`CONTAINER_WIDTHS` and the marketplace styling rule in CLAUDE.md: an
 * Ecommerce Pro SDK theme can retune the scrim (a brand may need a lighter wash, or a tinted one
 * rather than neutral black) without replacing the component. `none` stays a bare `transparent` --
 * it is the absence of a scrim rather than a color worth theming, and giving it a hook would invite
 * a theme to make "no overlay" paint something.
 */
export const OVERLAY_COLORS = {
  none: 'transparent',
  light: token('--dcm-cms-hero-overlay-light', 'rgba(0, 0, 0, 0.25)'),
  medium: token('--dcm-cms-hero-overlay-medium', 'rgba(0, 0, 0, 0.5)'),
  strong: token('--dcm-cms-hero-overlay-strong', 'rgba(0, 0, 0, 0.75)'),
} as const;

const VERTICAL_ALIGNMENT = {
  top: 'flex-start',
  center: 'center',
  bottom: 'flex-end',
} as const;

const HORIZONTAL_ALIGNMENT = {
  left: { marginLeft: 0, marginRight: 'auto', alignItems: 'flex-start' },
  center: { marginLeft: 'auto', marginRight: 'auto', alignItems: 'center' },
  right: { marginLeft: 'auto', marginRight: 0, alignItems: 'flex-end' },
} as const;

const OVERLAY_OPTIONS = [
  { label: 'None', value: 'none' },
  { label: 'Light', value: 'light' },
  { label: 'Medium', value: 'medium' },
  { label: 'Strong', value: 'strong' },
] as const;

const MIN_HEIGHT_OPTIONS = [
  { label: 'Auto', value: 'auto' },
  { label: 'Medium', value: 'medium' },
  { label: 'Tall', value: 'tall' },
  { label: 'Full screen', value: 'full' },
] as const;

const VERTICAL_ALIGN_OPTIONS = [
  { label: 'Top', value: 'top' },
  { label: 'Center', value: 'center' },
  { label: 'Bottom', value: 'bottom' },
] as const;

const HORIZONTAL_ALIGN_OPTIONS = [
  { label: 'Left', value: 'left' },
  { label: 'Center', value: 'center' },
  { label: 'Right', value: 'right' },
] as const;

const MAX_WIDTH_OPTIONS = [
  { label: 'Narrow', value: 'narrow' },
  { label: 'Normal', value: 'normal' },
  { label: 'Wide', value: 'wide' },
  { label: 'Full width', value: 'none' },
] as const;

const DEFAULT_PADDING: BoxSpacing = { top: '64px', right: '24px', bottom: '64px', left: '24px' };
const DEFAULT_HERO_BACKGROUND: BackgroundValue = { ...DEFAULT_BACKGROUND };

// Starter layout: a headline, supporting line and a call to action -- the canonical hero shape,
// centered to match the container's own default alignment.
//
// Built via `starterChild()` so each child carries its component's full `defaultProps`. That
// completeness is load-bearing rather than tidiness: Puck merges `defaultProps` for the inserted
// Hero but not for its slot children, and the field panel reads child props unmerged, so a prop
// left absent here shows an empty control and corrupts `object`-field edits (editing one padding
// side of a child with no `padding` renders `8px undefined undefined undefined`). See
// `StarterChild` in ./slot for the mechanism and `editor/default-layout-parity.test.tsx` for the
// guard.
//
// `href` is set explicitly rather than inheriting Button's `#` default: this button is labelled as
// an action, so an author who inserts a Hero and publishes without editing it would otherwise ship
// a live link that navigates nowhere.
//
// id is intentionally absent -- populateIds() stamps { id: generated, ...item.props }, so a
// hardcoded id would win and produce duplicate ids across every inserted Hero instance.
const HERO_STARTER_CHILDREN: StarterChild[] = [
  starterChild('Heading', { text: 'Your headline', level: 'h1', align: 'center' }),
  starterChild('Paragraph', {
    text: 'Add a short description that supports your headline.',
    align: 'center',
  }),
  starterChild('Button', { label: 'Shop now', href: '/shop', align: 'center' }),
];

const HeroContainer = styled.div`
  position: relative;
  isolation: isolate;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
  width: 100%;
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background: var(--cms-hero-overlay, transparent);
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  box-sizing: border-box;
`;

export type HeroProps = {
  content?: React.FC<SlotProps>;
  background: BackgroundValue;
  overlay: keyof typeof OVERLAY_COLORS;
  minHeight: keyof typeof CONTAINER_MIN_HEIGHTS;
  verticalAlign: keyof typeof VERTICAL_ALIGNMENT;
  horizontalAlign: keyof typeof HORIZONTAL_ALIGNMENT;
  textColor: ContainerTextColor;
  maxWidth: keyof typeof CONTAINER_WIDTHS;
  padding: BoxSpacing;
};

function resolveBackground(value: unknown): BackgroundValue {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return DEFAULT_HERO_BACKGROUND;
  const candidate = value as Partial<Record<keyof BackgroundValue, unknown>>;
  const stringValue = (key: keyof BackgroundValue): string =>
    typeof candidate[key] === 'string' ? candidate[key] : DEFAULT_HERO_BACKGROUND[key];

  return {
    color: stringValue('color'),
    image: stringValue('image'),
    size: stringValue('size'),
    position: stringValue('position'),
    repeat: stringValue('repeat'),
  };
}

function resolvePadding(value: unknown): BoxSpacing {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return DEFAULT_PADDING;
  const candidate = value as Partial<Record<keyof BoxSpacing, unknown>>;
  const side = (key: keyof BoxSpacing): string =>
    typeof candidate[key] === 'string' ? candidate[key] : DEFAULT_PADDING[key];

  return { top: side('top'), right: side('right'), bottom: side('bottom'), left: side('left') };
}

export const heroConfig: ComponentConfig<HeroProps> = {
  label: 'Hero',
  ai: {
    instructions:
      'A page-leading banner with a background and an editable content slot. Keep the Heading, Paragraph and Button concise. Use one h1 only when this is the page lead, and choose inverted text for a dark image or colour.',
  },
  fields: {
    content: { type: 'slot', allow: ['Heading', 'Paragraph', 'Button', 'Image'] },
    background: {
      type: 'custom',
      label: 'Background',
      render: BackgroundField,
      ai: {
        schema: BACKGROUND_AI_SCHEMA as Record<string, unknown>,
        instructions:
          "Background colour or image. Prefer a theme colour role; use 'cover' for photographic hero images.",
      },
    },
    overlay: selectField(
      'Image overlay',
      OVERLAY_OPTIONS,
      'Dark scrim over a background image for text legibility. It has no effect when there is no image.'
    ),
    minHeight: selectField(
      'Min height',
      MIN_HEIGHT_OPTIONS,
      "'Medium' (~320px) is the recommended default; 'Tall' (~480px) gives a more prominent banner."
    ),
    verticalAlign: selectField('Vertical alignment', VERTICAL_ALIGN_OPTIONS),
    horizontalAlign: selectField('Horizontal alignment', HORIZONTAL_ALIGN_OPTIONS),
    textColor: selectField(
      'Text color',
      CONTAINER_TEXT_COLOR_OPTIONS,
      "Use 'inverted' for text over a dark image or colour; otherwise inherit the page theme."
    ),
    maxWidth: selectField(
      'Content width',
      MAX_WIDTH_OPTIONS,
      "Constrains the content column. 'Narrow' keeps hero copy readable; 'Full width' spans the banner."
    ),
    padding: {
      type: 'object',
      label: 'Padding',
      objectFields: createSpacingFields(),
    },
  },
  defaultProps: {
    background: DEFAULT_HERO_BACKGROUND,
    overlay: 'medium',
    minHeight: 'medium',
    verticalAlign: 'center',
    horizontalAlign: 'center',
    textColor: INHERIT_COLOR,
    maxWidth: 'narrow',
    padding: DEFAULT_PADDING,
    content: (HERO_STARTER_CHILDREN as unknown) as React.FC<SlotProps>,
  },
  render: ({
    content: slotContent,
    background = DEFAULT_HERO_BACKGROUND,
    overlay = 'medium',
    minHeight = 'medium',
    verticalAlign = 'center',
    horizontalAlign = 'center',
    textColor = INHERIT_COLOR,
    maxWidth = 'narrow',
    padding = DEFAULT_PADDING,
  }) => {
    if (!slotContent) return <></>;
    const Slot = slotContent;
    const resolvedBackground = resolveBackground(background);
    const resolvedHorizontalAlign = lookup(HORIZONTAL_ALIGNMENT, horizontalAlign, HORIZONTAL_ALIGNMENT.center);
    const hasBackgroundImage = resolvedBackground.image.trim() !== '';

    return (
      <HeroContainer
        data-testid='cms-hero'
        style={{
          ...backgroundStyle(resolvedBackground),
          ...containerColorStyle(textColor),
          justifyContent: lookup(VERTICAL_ALIGNMENT, verticalAlign, VERTICAL_ALIGNMENT.center),
          minHeight: lookup(CONTAINER_MIN_HEIGHTS, minHeight, CONTAINER_MIN_HEIGHTS.medium),
          padding: spacingToCss(resolvePadding(padding)),
        }}
      >
        {hasBackgroundImage && overlay !== 'none' && (
          <HeroOverlay
            data-testid='cms-hero-overlay'
            aria-hidden='true'
            style={
              {
                '--cms-hero-overlay': lookup(OVERLAY_COLORS, overlay, OVERLAY_COLORS.medium),
              } as React.CSSProperties
            }
          />
        )}
        <Slot
          as={HeroContent}
          minEmptyHeight={120}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: BLOCK_SPACING.sm,
            width: '100%',
            maxWidth: lookup(CONTAINER_WIDTHS, maxWidth, CONTAINER_WIDTHS.narrow),
            ...resolvedHorizontalAlign,
          }}
        />
      </HeroContainer>
    );
  },
};
